import { useState, useEffect, useCallback } from 'react';
import { taskService, QuantifiedCompletedCountsResponse } from '../../../services/quantifiedTaskService';
import { queueHomeRequest } from '../homeRequestQueue';

export interface UseQuantifiedCompletedCountsResult {
  thisWeek: number;
  lastWeek: number;
  weekBoundaries: QuantifiedCompletedCountsResponse['weekBoundaries'];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface CompletedCountsFilterInput {
  foremanAreaNames?: string[];
  serviceAreaName?: string;
}

/**
 * Fetches completed task counts for the current and previous work week (Vancouver).
 * Optional filter scopes by foreman area names or service area name (same as other Home KPI widgets).
 */
export function useQuantifiedCompletedCounts(
  filter?: CompletedCountsFilterInput | null
): UseQuantifiedCompletedCountsResult {
  const [data, setData] = useState<{
    thisWeek: number;
    lastWeek: number;
    weekBoundaries: QuantifiedCompletedCountsResponse['weekBoundaries'];
  }>({ thisWeek: 0, lastWeek: 0, weekBoundaries: undefined });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const foremanAreaNames = filter?.foremanAreaNames;
  const serviceAreaName = filter?.serviceAreaName;
  const hasForemanFilter = foremanAreaNames && foremanAreaNames.length > 0;
  const hasServiceFilter = !!serviceAreaName;

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { foremanArea?: string; serviceArea?: string } = {};
      if (hasForemanFilter) {
        params.foremanArea = foremanAreaNames!.join(',');
      } else if (hasServiceFilter) {
        params.serviceArea = serviceAreaName!;
      }
      const response = await queueHomeRequest(() =>
        taskService.getQuantifiedCompletedCounts(Object.keys(params).length > 0 ? params : undefined)
      );
      setData({
        thisWeek: response.thisWeek,
        lastWeek: response.lastWeek,
        weekBoundaries: response.weekBoundaries,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch completed counts')
      );
      setData({ thisWeek: 0, lastWeek: 0, weekBoundaries: undefined });
    } finally {
      setLoading(false);
    }
  }, [
    hasForemanFilter ? foremanAreaNames!.join(',') : '',
    hasServiceFilter ? serviceAreaName : '',
  ]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return {
    thisWeek: data.thisWeek,
    lastWeek: data.lastWeek,
    weekBoundaries: data.weekBoundaries,
    loading,
    error,
    refetch: fetchCounts,
  };
}
